import { BrandData } from '@/context/BrandContext';

export interface ShortcodeData {
  brandName: string;
  brandUrl: string;
  publishedDate: string;
  designCredit: string;
  brandLogo: string;
}

/**
 * Process shortcodes in text content and replace them with actual brand data
 * @param text - The text containing shortcodes
 * @param brandData - The brand data to use for replacement
 * @returns Processed text with shortcodes replaced
 */
export function processShortcodes(text: string, brandData: BrandData | null, defaultDesignCredit: string = 'AgencyPlus'): string {
  if (!text || !brandData) {
    return text;
  }

  // Get current date in mm-dd-yyyy format for publishedDate
  const getCurrentDate = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const year = now.getFullYear();
    return `${month}-${day}-${year}`;
  };

  // Define shortcode mappings
  const shortcodeMap: Record<string, string> = {
    '{{brandName}}': brandData.name || 'Brand Name',
    '{{brandUrl}}': brandData.brandUrl || 'brand.com',
    '{{publishedDate}}': getCurrentDate(),
    '{{designCredit}}': brandData.designCredit || defaultDesignCredit,
    '{{brandLogo}}': brandData.brandLogo || ''
  };

  // Replace all shortcodes
  let processedText = text;
  Object.entries(shortcodeMap).forEach(([shortcode, value]) => {
    processedText = processedText.replace(new RegExp(shortcode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
  });

  return processedText;
}

/**
 * Check if text contains any shortcodes
 * @param text - The text to check
 * @returns True if text contains shortcodes
 */
export function containsShortcodes(text: string): boolean {
  const shortcodePattern = /\{\{[^}]+\}\}/;
  return shortcodePattern.test(text);
}

/**
 * Get all shortcodes found in text
 * @param text - The text to extract shortcodes from
 * @returns Array of shortcodes found
 */
export function extractShortcodes(text: string): string[] {
  const shortcodePattern = /\{\{[^}]+\}\}/g;
  const matches = text.match(shortcodePattern);
  return matches || [];
}

/**
 * Get available shortcodes for reference
 * @returns Array of available shortcodes
 */
export function getAvailableShortcodes(): string[] {
  return [
    '{{brandName}}',
    '{{brandUrl}}',
    '{{publishedDate}}',
    '{{designCredit}}',
    '{{brandLogo}}'
  ];
}
